import {Options, XAxisPlotLinesOptions} from 'highcharts';
import React, {useCallback, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {linearRegression, linearRegressionLine} from 'simple-statistics';

import {ChartOptionInterface} from '@veneer/core';

import {Renewals} from '@br/idm-api';
import {Chart, useIntlFormatter, useTooltipFormatter} from '@br/idm-components';
import {useWolfTheme} from '@br/mg-components';

const RenewalsChart = ({renewals}: {renewals: Renewals}): JSX.Element => {
    const {t} = useTranslation(['renewals']);
    const {rtl, formatNumber} = useIntlFormatter();
    const theme = useWolfTheme();

    const historicDaysCount = -renewals.day[0];

    /* istanbul ignore next */
    const formatXValue = useCallback(
        (x: number): string => {
            return x === 0
                ? t('renewals:today', 'Today')
                : t('renewals:nDays', {
                      count: Math.abs(x),
                      days: formatNumber(x, {signDisplay: 'exceptZero'}),
                      defaultValue: '{{days}} day',
                      defaultValue_other: '{{days}} days',
                  });
        },
        [formatNumber, t]
    );

    // eslint-disable-next-line i18next/no-literal-string
    const format = useTooltipFormatter('bar', formatXValue);

    const deviceTrend = useMemo(() => {
        return linearRegressionLine(
            linearRegression(
                renewals.day.slice(0, historicDaysCount + 1).map((day, index) => [day, renewals.seats[index]])
            )
        );
    }, [renewals, historicDaysCount]);

    const plotLines: XAxisPlotLinesOptions[] = [
        {
            value: 0,
            dashStyle: 'Dash',
            label: {
                text: t('renewals:today', 'Today'),
                verticalAlign: 'middle',
                textAlign: 'center',
            },
        },
    ];

    if (renewals.renewBefore !== null && renewals.renewBefore <= renewals.day[renewals.day.length - 1]) {
        plotLines.push({
            value: renewals.renewBefore,
            dashStyle: 'Dash',
            color: theme.palette.negative.default,
            label: {
                text: t('renewals:renewBefore', 'Renew before'),
                verticalAlign: 'middle',
                textAlign: 'center',
            },
        });
    }

    const options: Omit<ChartOptionInterface, keyof Options> & Options = {
        chart: {
            type: 'line',
            animation: false,
        },
        legend: {
            enabled: true,
            rtl,
        },
        tooltip: {
            shared: true,
            formatter: format,
        },
        plotOptions: {
            series: {
                showInLegend: true,
                pointStart: renewals.day[0],
                marker: {
                    enabled: false,
                },
            },
        },
        yAxis: {
            min: 0,
            allowDecimals: false,
            title: {
                text: t('renewals:seatDeviceCount', 'Seat/Device count'),
            },
        },
        xAxis: {
            plotLines,
            title: {
                text: t('renewals:days', 'Days'),
            },
            labels: {
                formatter() {
                    return formatNumber(this.pos, {signDisplay: 'exceptZero'});
                },
            },
        },
        series: [
            {
                name: t('renewals:licenseSeats', 'License seats'),
                type: 'line',
                data: renewals.licenses,
                color: theme.palette.primary.default,
                step: 'left',
            },
            {
                name: t('renewals:deviceCountActual', 'Device count (actual)'),
                type: 'area',
                data: renewals.seats.slice(0, historicDaysCount + 1),
                color: theme.palette.positive.default,
                step: 'left',
            },
            {
                name: t('renewals:deviceCountProjected', 'Device count (projected)'),
                type: 'area',
                data: [...new Array(historicDaysCount).fill(null), ...renewals.seats.slice(historicDaysCount)],
                color: theme.palette.positive.background,
                fillColor: theme.palette.positive.background,
                step: 'left',
            },
            {
                name: t('renewals:deviceCountTrend', 'Device count trend (30 days)'),
                type: 'line',
                data: renewals.day.map(deviceTrend),
                color: theme.palette.positive.default,
                dashStyle: 'Dash',
                enableMouseTracking: false,
            },
        ],
    };
    return (
        <Chart
            type="custom"
            options={options}
        />
    );
};

export default RenewalsChart;
